<template>
  <div class="photoelectric-current-viz">
    
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula" v-latex>$<strong>公式：</strong>U_s = (hν - W)/e | 遏止电压与饱和电流$</div>
    </div>

    <div class="main-layout vertical">
      <div class="canvas-box simulation">
        <svg width="100%" height="240" viewBox="0 0 400 240">
          <!-- Vacuum Tube -->
          <ellipse cx="200" cy="120" rx="150" ry="80" fill="none" stroke="#888" stroke-dasharray="5,5" />
          
          <!-- Cathode (Left) -->
          <rect x="80" y="80" width="10" height="80" fill="#2f3542" stroke="#fff" />
          <text x="60" y="70" fill="#fff" font-size="8">阴极 (K)</text>

          <!-- Anode (Right) -->
          <rect x="310" y="80" width="10" height="80" fill="#2f3542" stroke="#fff" />
          <text x="310" y="70" fill="#fff" font-size="8">阳极 (A)</text>

          <!-- Photons -->
          <g v-for="p in photons" :key="'p'+p.id">
             <path d="M 0,0 Q 10,-5 20,0 T 40,0" fill="none" :stroke="lightColor" stroke-width="2" 
                   :transform="`translate(${p.x}, ${p.y}) rotate(20)`" />
          </g>

          <!-- Electrons -->
          <g v-for="e in electrons" :key="'e'+e.id">
             <circle :cx="e.x" :cy="e.y" r="3" fill="#00d2ff" />
             <line :x1="e.x - 2" :y1="e.y" :x2="e.x + 2" :y2="e.y" stroke="#fff" stroke-width="0.5" />
          </g>

          <!-- Voltage Label -->
          <text x="180" y="220" :fill="voltage >= 0 ? '#2ed573' : '#ff4757'" font-size="10">U = {{ voltage }} V</text>
        </svg>

        <div class="hud-top">
           <div class="stat">
              <label>光电流 I: {{ current.toFixed(2) }} μA</label>
           </div>
        </div>
      </div>

      <div class="bottom-controls">
        <div class="control-card glass">
          <div class="row items-center">
             <label>调节加电压</label>
             <el-slider v-model="voltage" :min="-10" :max="10" size="mini"></el-slider>
          </div>
          <div class="row items-center mt-2">
             <label>入射光强度</label>
             <el-slider v-model="intensity" :min="0" :max="100" size="mini"></el-slider>
          </div>
        </div>

        <div class="discovery-note" v-latex>
           <p><strong>物理详解：</strong>光电流的伏安特性曲线揭示了光电效应的量子本质。当频率高于截止频率的光照射阴极时，逸出的光电子在电场作用下飞向阳极形成**光电流**。
           1. **饱和电流**：当加速电压足够大时，所有逸出的电子都被收集，电流达到最大值，其大小正比于**光强**。
           2. **遏止电压**：当施加反向电压且恰好使光电流归零时，其大小仅取决于**光频**，而与光强无关。
           这有力地证明了爱因斯坦的光子假说。</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PhotoelectricCurrentViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      voltage: 5,
      intensity: 50,
      electrons: [],
      photons: [],
      t: 0,
      timer: null
    }
  },
  computed: {
    current() {
       if (this.voltage < -3) return 0; // Us = -3 approx
       if (this.voltage >= 5) return this.intensity * 0.5;
       return (this.intensity * 0.5) * (this.voltage + 3) / 8;
    },
    lightColor() { return '#ffd700'; }
  },
  methods: {
    update() {
       this.t++;
       // Spawn photons
       if (this.t % 5 === 0 && this.intensity > 0) {
          this.photons.push({ id: Math.random(), x: 20, y: Math.random() * 200, vx: 5 });
       }
       // Photon hitting cathode -> Electron
       this.photons.forEach((p, idx) => {
          p.x += p.vx;
          if (p.x > 80 && p.x < 100) {
             this.photons.splice(idx, 1);
             if (Math.random() * 100 < this.intensity) {
                this.electrons.push({ id: Math.random(), x: 90, y: p.y, vx: 1 + Math.random() * 2 });
             }
          }
          if (p.x > 400) this.photons.splice(idx, 1);
       });

       // Electron motion
       this.electrons.forEach((e, idx) => {
          let accel = this.voltage * 0.05;
          e.vx += accel;
          e.x += e.vx;
          // Reverse motion check
          if (e.vx < 0 && e.x < 80) this.electrons.splice(idx, 1);
          if (e.x > 310) {
             this.electrons.splice(idx, 1);
          }
          if (e.x < 0 || e.x > 400) this.electrons.splice(idx, 1);
       });

       this.timer = requestAnimationFrame(this.update);
    }
  },
  mounted() { this.update(); },
  beforeDestroy() { cancelAnimationFrame(this.timer); }
}
</script>

<style scoped lang="scss">
.photoelectric-current-viz { flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden; }
.canvas-box { background: #020205; border-radius: 12px; position: relative; border: 1px solid rgba(255,255,255,0.05); }
.bottom-controls { flex: 0.6; display: flex; flex-direction: column; gap: 10px; }
.control-card { background: rgba(255,255,255,0.02); border-radius: 12px; padding: 15px; }
.discovery-note { background: rgba(0, 210, 255, 0.05); padding: 12px 18px; border-radius: 12px; p { margin: 0; font-size: 0.75rem; color: #aaa; line-height: 1.5; strong { color: #fff; } } }
</style>
